/** QuickPlan — Préparation des pièces avant montage : outillage et consignes de perçage /
 *  d'usinage pièce par pièce, tirées des perçages et encoches de la notice. */
import { recessText } from './drawing.js';
import { sheetRange } from './templates.js';

const fmt = (v) => String(Math.round(v * 10) / 10).replace('.', ',');

/** Rôle d'un trou en clair. */
function holeAction(h) {
  const d = `Ø${fmt(h.dia)}`;
  switch (h.type) {
    case 'tourillon': return `percer ${d} à ${fmt(h.depth)} mm de profondeur pour tourillon (collé au montage)`;
    case 'cheville': return `percer ${d} à ${fmt(h.depth)} mm pour cheville`;
    case 'boulon': return `percer ${d} de part en part (passage de boulon)`;
    case 'rivet': return `percer ${d} de part en part (rivet)`;
    case 'excentrique': return `percer ${d} à ${fmt(h.depth)} mm (logement d'excentrique, mèche à façonner)`;
    case 'percage': return h.through ? `percer ${d} de part en part` : `percer ${d} à ${fmt(h.depth)} mm de profondeur`;
    default:
      return h.through
        ? `percer ${d} de part en part (passage de vis${/confirmat/i.test(h.what ?? '') ? ', fraiser l’entrée' : ''})`
        : `avant-trou ${d} à ${fmt(h.depth)} mm de profondeur`;
  }
}

/** Consignes d'une pièce : [{ face, label, lines: [texte], sheets }]. */
export function piecePrep(piece, sheets) {
  return piece.faces.map((f) => {
    const groups = new Map();
    for (const h of f.holes) {
      const k = `${holeAction(h)}|${h.what ?? ''}`;
      const g = groups.get(k) ?? { action: holeAction(h), what: h.what, count: 0 };
      g.count++;
      groups.set(k, g);
    }
    const lines = [...groups.values()].map((g) => `${g.count} trou${g.count > 1 ? 's' : ''} : ${g.action}${g.what ? ` — ${g.what}` : ''}`);
    const rec = new Map();
    for (const r of f.recesses ?? []) {
      const k = recessText(r);
      rec.set(k, (rec.get(k) ?? 0) + 1);
    }
    for (const [k, n] of rec) lines.push(`${n} × ${k} : usiner à la défonceuse (fraise droite) ou au ciseau à bois`);
    return { face: f.code, label: f.label, lines, sheets: sheets ? sheetRange(sheets, piece, f.code) : '' };
  });
}

/** Outillage : forets (Ø → profondeur maxi, traversant ?) et usinages. */
export function tooling(notice) {
  const drills = new Map();
  let recesses = 0;
  let maxRecessDepth = 0;
  for (const p of notice.pieces) {
    for (const f of p.faces) {
      for (const h of f.holes) {
        const d = drills.get(h.dia) ?? { dia: h.dia, depth: 0, through: false, count: 0 };
        d.count += p.qty;
        if (h.through) d.through = true;
        else d.depth = Math.max(d.depth, h.depth);
        drills.set(h.dia, d);
      }
      for (const r of f.recesses ?? []) { recesses += p.qty; maxRecessDepth = Math.max(maxRecessDepth, r.depth); }
    }
  }
  const lines = [...drills.values()].sort((a, b) => a.dia - b.dia).map((d) => {
    const how = [d.depth ? `butée de profondeur à ${fmt(d.depth)} mm` : '', d.through ? 'trous traversants : martyr dessous' : ''].filter(Boolean).join(', ');
    return `Foret Ø${fmt(d.dia)} — ${d.count} trou${d.count > 1 ? 's' : ''}${how ? ` (${how})` : ''}`;
  });
  if (recesses) lines.push(`Défonceuse avec fraise droite (ou ciseau à bois) — ${recesses} encoche${recesses > 1 ? 's' : ''}, profondeur ${fmt(maxRecessDepth)} mm maxi`);
  if (lines.length) lines.push('Crayon, pointeau, ruban adhésif, équerre ; perceuse en position bien perpendiculaire (support ou guide de perçage)');
  return lines;
}

/** Conseils généraux (début de la préparation). */
export const PREP_TIPS = [
  'Avant tout perçage, inscrire au crayon la lettre de chaque pièce (inventaire) sur une face qui restera cachée.',
  'Imprimer les gabarits à 100 % (taille réelle) et vérifier le carré de contrôle de 30 mm.',
  'Poser chaque gabarit sur la face indiquée en alignant ses traits épais sur les chants nommés, le scotcher, pointer les centres, retirer puis percer.',
  'Pour un trou borgne, régler la butée de profondeur ; pour un trou traversant, placer une planche martyre dessous pour ne pas faire éclater la sortie.',
];
